import React, { useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SEOIntelligence = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [dashboard, setDashboard] = useState(null);
  const [clusters, setClusters] = useState({});
  const [contentSuggestions, setContentSuggestions] = useState([]);
  const [schemas, setSchemas] = useState([]);
  const [rankings, setRankings] = useState(null);
  const [rrfAnalysis, setRrfAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [dashRes, clustersRes, suggestionsRes, schemasRes, rankingsRes, rrfRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/seo-intelligence/dashboard`),
        axios.get(`${BACKEND_URL}/api/seo-intelligence/topic-clusters`),
        axios.get(`${BACKEND_URL}/api/seo-intelligence/content-suggestions`),
        axios.get(`${BACKEND_URL}/api/seo-intelligence/schemas`),
        axios.get(`${BACKEND_URL}/api/seo-intelligence/chatgpt-rankings`),
        axios.get(`${BACKEND_URL}/api/seo-intelligence/rrf-analysis`)
      ]);
      
      setDashboard(dashRes.data);
      setClusters(clustersRes.data.clusters || {});
      setContentSuggestions(suggestionsRes.data.suggestions || []);
      setSchemas(schemasRes.data.schemas || []);
      setRankings(rankingsRes.data);
      setRrfAnalysis(rrfRes.data);
    } catch (error) {
      console.error('Failed to fetch SEO data:', error);
    } finally {
      setLoading(false);
    }
  };

  const analyzeAllClusters = async () => {
    setAnalyzing(true);
    try {
      await axios.post(`${BACKEND_URL}/api/seo-intelligence/analyze-all-clusters`);
      fetchData();
      alert('All clusters analyzed successfully!');
    } catch (error) {
      alert('Failed to analyze clusters');
    } finally {
      setAnalyzing(false);
    }
  };

  const generateContentSuggestions = async (clusterName) => {
    setGenerating(true);
    try {
      await axios.post(`${BACKEND_URL}/api/seo-intelligence/generate-content-suggestions/${clusterName}?count=5`);
      fetchData();
      alert('Content suggestions generated!');
    } catch (error) {
      alert('Failed to generate suggestions');
    } finally {
      setGenerating(false);
    }
  };

  const generateSchema = async (pageType, pageUrl) => {
    try {
      const response = await axios.post(`${BACKEND_URL}/api/seo-intelligence/generate-schema/${pageType}?page_url=${encodeURIComponent(pageUrl)}`);
      fetchData();
      alert('Schema generated! Check the Schemas tab.');
      return response.data;
    } catch (error) {
      alert('Failed to generate schema');
    }
  };

  const updateSuggestionStatus = async (id, status) => {
    try {
      await axios.put(`${BACKEND_URL}/api/seo-intelligence/content-suggestions/${id}/status?status=${status}`);
      fetchData();
    } catch (error) {
      alert('Failed to update status');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert('Copied to clipboard!');
  };

  const getRrfColor = (score) => {
    if (score >= 0.6) return 'text-green-600';
    if (score >= 0.4) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getPriorityBadge = (priority) => {
    const styles = {
      'high': 'bg-red-100 text-red-700',
      'medium': 'bg-yellow-100 text-yellow-700',
      'low': 'bg-gray-100 text-gray-600'
    };
    return styles[priority] || 'bg-gray-100 text-gray-600';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-start mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">🧠 SEO Intelligence Suite</h1>
          <p className="text-gray-600">Reciprocal Rank Fusion (RRF) optimization for ChatGPT Search</p>
        </div>
        <button
          onClick={analyzeAllClusters}
          disabled={analyzing}
          className="bg-teal-600 text-white px-4 py-2 rounded-lg hover:bg-teal-700 disabled:opacity-50"
        >
          {analyzing ? '🔄 Analyzing...' : '📊 Analyze All Clusters'}
        </button>
      </div>

      {/* RRF Score Banner */}
      {rrfAnalysis && (
        <div className="bg-gradient-to-r from-purple-600 to-blue-600 rounded-lg p-6 mb-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg opacity-80">Overall RRF Score</h2>
              <p className="text-5xl font-bold">{rrfAnalysis.overall_rrf_score}</p>
              <p className="opacity-80">/ 1.0 (higher = better ChatGPT visibility)</p>
            </div>
            <div className="text-right">
              <p className="text-lg">Average Coverage: <strong>{rrfAnalysis.average_coverage}%</strong></p>
              <p className="opacity-80">{rrfAnalysis.clusters_analyzed}/{rrfAnalysis.total_clusters} clusters analyzed</p>
            </div>
          </div>
        </div>
      )}

      {/* Stats Grid */}
      {dashboard?.summary && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
          <div className="bg-white p-4 rounded-lg shadow border-l-4 border-purple-500">
            <p className="text-gray-600 text-sm">Topic Clusters</p>
            <p className="text-2xl font-bold text-purple-600">{dashboard.summary.topic_clusters}</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow border-l-4 border-blue-500">
            <p className="text-gray-600 text-sm">Analyzed</p>
            <p className="text-2xl font-bold text-blue-600">{dashboard.summary.clusters_analyzed}</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow border-l-4 border-orange-500">
            <p className="text-gray-600 text-sm">Content Ideas</p>
            <p className="text-2xl font-bold text-orange-600">{dashboard.summary.content_suggestions}</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow border-l-4 border-green-500">
            <p className="text-gray-600 text-sm">Published</p>
            <p className="text-2xl font-bold text-green-600">{dashboard.summary.content_published}</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow border-l-4 border-teal-500">
            <p className="text-gray-600 text-sm">ChatGPT Citations</p>
            <p className="text-2xl font-bold text-teal-600">{dashboard.summary.chatgpt_citations}</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow border-l-4 border-pink-500">
            <p className="text-gray-600 text-sm">Citation Rate</p>
            <p className="text-2xl font-bold text-pink-600">{dashboard.summary.citation_rate}%</p>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex border-b border-gray-200 mb-6 overflow-x-auto">
        {[
          { id: 'dashboard', label: '📊 Dashboard' },
          { id: 'clusters', label: '🎯 Topic Clusters' },
          { id: 'content', label: '📝 Content Ideas' },
          { id: 'schema', label: '🔧 Schema Markup' },
          { id: 'chatgpt', label: '🤖 ChatGPT Tracker' },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 font-medium whitespace-nowrap ${activeTab === tab.id ? 'text-teal-600 border-b-2 border-teal-600' : 'text-gray-500 hover:text-gray-700'}`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Dashboard Tab */}
      {activeTab === 'dashboard' && (
        <div className="space-y-6">
          {/* What is RRF */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="font-semibold text-blue-800 mb-2">🎓 What is Reciprocal Rank Fusion (RRF)?</h3>
            <p className="text-sm text-blue-700 mb-2">
              RRF is how ChatGPT Search ranks results. Instead of rewarding sites that rank #1 for one keyword,
              it favors sites that rank <strong>consistently across multiple related queries</strong>.
            </p>
            <p className="text-sm text-blue-700">
              <strong>Example:</strong> A site ranking #4 for "dental implants", "implant cost", and "implant recovery"
              will outrank a site that's #1 for "dental implants" but #50 for related terms.
            </p>
          </div>

          {/* Weakest Clusters */}
          {rrfAnalysis?.weakest_clusters?.length > 0 && (
            <div className="bg-white rounded-lg shadow p-4">
              <h3 className="font-semibold text-gray-800 mb-3">⚠️ Priority: Weakest Clusters</h3>
              <div className="space-y-2">
                {rrfAnalysis.weakest_clusters.map((cluster, i) => (
                  <div key={i} className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                    <div>
                      <span className="font-medium">{cluster.pillar}</span>
                      <span className="text-sm text-gray-500 ml-2">Coverage: {cluster.coverage_percentage}%</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`font-bold ${getRrfColor(cluster.rrf_score)}`}>
                        RRF: {cluster.rrf_score}
                      </span>
                      <button
                        onClick={() => generateContentSuggestions(cluster.cluster)}
                        className="text-teal-600 hover:text-teal-700 text-sm"
                      >
                        Generate Content →
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Recommendations */}
          {rrfAnalysis?.recommendations && (
            <div className="bg-white rounded-lg shadow p-4">
              <h3 className="font-semibold text-gray-800 mb-3">💡 Recommendations</h3>
              <ul className="space-y-2">
                {rrfAnalysis.recommendations.map((rec, i) => (
                  <li key={i} className="flex items-start gap-2 text-gray-700">
                    <span className="text-teal-500">✓</span>
                    {rec}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Topic Clusters Tab */}
      {activeTab === 'clusters' && (
        <div className="space-y-4">
          {Object.entries(clusters).map(([key, cluster]) => (
            <div key={key} className="bg-white rounded-lg shadow overflow-hidden">
              <div className="p-4 border-b border-gray-200 flex justify-between items-center">
                <div>
                  <h3 className="font-semibold text-gray-800">{cluster.pillar}</h3>
                  <p className="text-sm text-gray-500">{cluster.description}</p>
                </div>
                <button
                  onClick={() => generateContentSuggestions(key)}
                  disabled={generating}
                  className="bg-teal-600 text-white px-3 py-1 rounded-lg text-sm hover:bg-teal-700 disabled:opacity-50"
                >
                  {generating ? 'Generating...' : '+ Generate Content Ideas'}
                </button>
              </div>
              <div className="p-4">
                <div className="mb-3">
                  <p className="text-xs text-gray-500 uppercase mb-1">Primary Keywords</p>
                  <div className="flex flex-wrap gap-1">
                    {cluster.primary_keywords.map((kw, i) => (
                      <span key={i} className="bg-teal-100 text-teal-700 px-2 py-0.5 rounded text-xs">{kw}</span>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase mb-1">Cluster Topics ({cluster.cluster_topics.length})</p>
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-2">
                    {cluster.cluster_topics.map((topic, i) => (
                      <div key={i} className="bg-gray-50 p-2 rounded text-sm">
                        <p className="font-medium text-gray-700">{topic.topic}</p>
                        <p className="text-xs text-gray-500">{topic.keywords.length} keywords</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Content Ideas Tab */}
      {activeTab === 'content' && (
        <div className="space-y-4">
          {contentSuggestions.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-8 text-center">
              <p className="text-gray-500 mb-4">No content suggestions yet. Generate some from the Topic Clusters tab!</p>
              <button
                onClick={() => setActiveTab('clusters')}
                className="bg-teal-600 text-white px-4 py-2 rounded-lg hover:bg-teal-700"
              >
                Go to Topic Clusters
              </button>
            </div>
          ) : (
            contentSuggestions.map((suggestion) => (
              <div key={suggestion.id} className="bg-white rounded-lg shadow p-4">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <h3 className="font-semibold text-gray-800">{suggestion.suggested_title}</h3>
                    <p className="text-sm text-gray-500">
                      {suggestion.pillar} → {suggestion.topic}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-0.5 rounded text-xs ${getPriorityBadge(suggestion.priority)}`}>
                      {suggestion.priority}
                    </span>
                    <select
                      value={suggestion.status}
                      onChange={(e) => updateSuggestionStatus(suggestion.id, e.target.value)}
                      className="text-sm border rounded px-2 py-1"
                    >
                      <option value="suggested">Suggested</option>
                      <option value="in_progress">In Progress</option>
                      <option value="published">Published</option>
                    </select>
                  </div>
                </div>
                <div className="mb-3">
                  <p className="text-xs text-gray-500 uppercase mb-1">Target Keywords</p>
                  <div className="flex flex-wrap gap-1">
                    {suggestion.keywords.slice(0, 5).map((kw, i) => (
                      <span key={i} className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded text-xs">{kw}</span>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase mb-1">Suggested Outline</p>
                  <ol className="list-decimal list-inside text-sm text-gray-600">
                    {suggestion.suggested_outline.map((item, i) => (
                      <li key={i}>{item}</li>
                    ))}
                  </ol>
                </div>
                <div className="mt-3 pt-3 border-t border-gray-100 flex justify-between items-center">
                  <span className="text-sm text-gray-500">Target: {suggestion.target_word_count} words</span>
                  <button
                    onClick={() => copyToClipboard(JSON.stringify(suggestion, null, 2))}
                    className="text-teal-600 hover:text-teal-700 text-sm"
                  >
                    📋 Copy Details
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Schema Markup Tab */}
      {activeTab === 'schema' && (
        <div className="space-y-6">
          {/* Quick Generate */}
          <div className="bg-white rounded-lg shadow p-4">
            <h3 className="font-semibold text-gray-800 mb-3">🔧 Generate Schema Markup</h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3">
              {[
                { type: 'local_business', label: 'Local Business', url: 'https://fortworthdental.com' },
                { type: 'dental_implants', label: 'Dental Implants', url: 'https://fortworthdental.com/implant-center/dental-implants' },
                { type: 'faq', label: 'FAQ Schema', url: 'https://fortworthdental.com/implant-center/dental-implants' },
                { type: 'lanap', label: 'LANAP Procedure', url: 'https://fortworthdental.com/lanap-gum-treatment' },
              ].map((item) => (
                <button
                  key={item.type}
                  onClick={() => generateSchema(item.type, item.url)}
                  className="bg-gray-100 hover:bg-gray-200 p-3 rounded-lg text-left"
                >
                  <p className="font-medium text-gray-800">{item.label}</p>
                  <p className="text-xs text-gray-500 truncate">{item.url}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Generated Schemas */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="p-4 border-b border-gray-200">
              <h3 className="font-semibold text-gray-800">📄 Generated Schemas</h3>
            </div>
            {schemas.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                No schemas generated yet. Click a button above to generate.
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {schemas.map((schema) => (
                  <div key={schema.id} className="p-4">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <span className="bg-purple-100 text-purple-700 px-2 py-0.5 rounded text-xs">
                          {schema.schema_type}
                        </span>
                        <p className="text-sm text-gray-600 mt-1">{schema.page_url}</p>
                      </div>
                      <button
                        onClick={() => copyToClipboard(`<script type="application/ld+json">\n${JSON.stringify(schema.schema_json, null, 2)}\n</script>`)}
                        className="bg-teal-600 text-white px-3 py-1 rounded text-sm hover:bg-teal-700"
                      >
                        📋 Copy Code
                      </button>
                    </div>
                    <pre className="bg-gray-50 p-3 rounded text-xs overflow-x-auto max-h-40">
                      {JSON.stringify(schema.schema_json, null, 2)}
                    </pre>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ChatGPT Tracker Tab */}
      {activeTab === 'chatgpt' && (
        <div className="space-y-6">
          {/* Stats */}
          {rankings && (
            <div className="grid md:grid-cols-3 gap-4">
              <div className="bg-white p-4 rounded-lg shadow border-l-4 border-green-500">
                <p className="text-gray-600 text-sm">Queries Tracked</p>
                <p className="text-2xl font-bold text-green-600">{rankings.total}</p>
              </div>
              <div className="bg-white p-4 rounded-lg shadow border-l-4 border-teal-500">
                <p className="text-gray-600 text-sm">Times Cited</p>
                <p className="text-2xl font-bold text-teal-600">{rankings.cited}</p>
              </div>
              <div className="bg-white p-4 rounded-lg shadow border-l-4 border-purple-500">
                <p className="text-gray-600 text-sm">Citation Rate</p>
                <p className="text-2xl font-bold text-purple-600">{rankings.citation_rate}%</p>
              </div>
            </div>
          )}

          {/* How to Track */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <h3 className="font-semibold text-yellow-800 mb-2">📝 How to Track ChatGPT Rankings</h3>
            <ol className="list-decimal list-inside text-sm text-yellow-700 space-y-1">
              <li>Open ChatGPT and search for queries like "best dental implant dentist Fort Worth"</li>
              <li>Check if fortworthdental.com is cited in the response</li>
              <li>Note your position if mentioned (1st source, 2nd source, etc.)</li>
              <li>Record competitor citations to track share of voice</li>
            </ol>
          </div>

          {/* Tracked Queries */}
          {rankings?.entries?.length > 0 ? (
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="p-4 border-b border-gray-200">
                <h3 className="font-semibold text-gray-800">🔍 Tracked Queries</h3>
              </div>
              <table className="min-w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Query</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cited?</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Position</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Checked</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {rankings.entries.map((entry) => (
                    <tr key={entry.id}>
                      <td className="px-4 py-3 text-sm text-gray-700">{entry.query}</td>
                      <td className="px-4 py-3">
                        {entry.cited ? (
                          <span className="bg-green-100 text-green-700 px-2 py-0.5 rounded text-xs">✓ Yes</span>
                        ) : (
                          <span className="bg-gray-100 text-gray-600 px-2 py-0.5 rounded text-xs">No</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">{entry.position || '-'}</td>
                      <td className="px-4 py-3 text-sm text-gray-500">
                        {new Date(entry.checked_at).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
              No queries tracked yet. Use the API or manual tracking to add data.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SEOIntelligence;
